/**
 * ComboboxSelector - Reusable Dropdown with Search
 */
import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, Check, X } from 'lucide-react';

interface ComboboxOption {
  id: number;
  label: string;
  subLabel?: string;
  searchValue?: string; // Custom string for searching
}

interface ComboboxSelectorProps {
  options: ComboboxOption[];
  value: number;
  onChange: (id: number) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  label?: string;
  error?: string;
  className?: string;
}

export const ComboboxSelector: React.FC<ComboboxSelectorProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Selecione...',
  searchPlaceholder = 'Buscar...',
  disabled = false,
  isLoading = false,
  label,
  error,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options (with defensive checks for undefined values)
  const filteredOptions = options.filter((option) => {
    if (!option || !option.label) return false;
    const term = searchTerm.toLowerCase();
    const labelMatch = option.label.toLowerCase().includes(term);
    const subLabelMatch = option.subLabel ? option.subLabel.toLowerCase().includes(term) : false;
    const customMatch = option.searchValue ? option.searchValue.toLowerCase().includes(term) : false;
    return labelMatch || subLabelMatch || customMatch;
  });

  const selectedOption = options.find((o) => o && o.id === value);

  const handleSelect = (id: number) => {
    onChange(id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const toggleOpen = () => {
    if (!disabled && !isLoading) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        // Focus search input when opening
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      {label && (
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </label>
      )}
      
      {/* Trigger Button */}
      <div
        onClick={toggleOpen}
        className={`
          w-full px-4 py-3 flex items-center justify-between border-2 rounded-lg cursor-pointer transition-all
          ${disabled || isLoading 
            ? 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-500 cursor-not-allowed' 
            : isOpen 
              ? 'border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900/30 bg-white dark:bg-gray-700 text-gray-900 dark:text-white' 
              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
          }
          ${error ? 'border-red-400 dark:border-red-500 ring-2 ring-red-100 dark:ring-red-900/30' : ''}
        `}
      >
        <div className="flex-1 truncate">
          {selectedOption ? (
            <div className="flex flex-col items-start leading-tight">
              <span className="font-medium">{selectedOption.label}</span>
              {selectedOption.subLabel && (
                <span className="text-xs text-gray-500 dark:text-gray-400">{selectedOption.subLabel}</span>
              )}
            </div>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">
              {isLoading ? 'Carregando...' : placeholder}
            </span>
          )}
        </div>
        <ChevronDown 
          size={18} 
          className={`ml-2 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden animation-fade-in-down">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-8 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:border-blue-500 dark:focus:border-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
            {filteredOptions.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                Nenhum resultado encontrado
              </div>
            ) : (
              <ul>
                {filteredOptions.map((option) => (
                  <li
                    key={option.id}
                    onClick={() => handleSelect(option.id)}
                    className={`
                      px-4 py-2.5 cursor-pointer text-sm transition-colors
                      flex items-center justify-between group
                      ${value === option.id 
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' 
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }
                    `}
                  >
                    <div className="flex flex-col">
                      <span className={`font-medium ${value === option.id ? 'text-blue-700 dark:text-blue-300' : 'group-hover:text-gray-900 dark:group-hover:text-white'}`}>
                        {option.label}
                      </span>
                      {option.subLabel && (
                        <span className={`text-xs mt-0.5 ${value === option.id ? 'text-blue-500 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          {option.subLabel}
                        </span>
                      )}
                    </div>
                    {value === option.id && <Check size={16} className="text-blue-600 dark:text-blue-400" />}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
};